import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Plus, BookOpen, Video, FileText, Activity, Filter } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/context/AuthContext';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const EducationPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [filters, setFilters] = useState({ category: '', difficulty_level: '' });
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    content_type: 'article',
    difficulty_level: 'beginner',
    category: 'cognitive',
    content_url: '',
    thumbnail_url: '',
    duration_minutes: ''
  });

  useEffect(() => {
    if (!token) {
      navigate('/auth');
      return;
    }
    fetchContent();
  }, [token, filters]);

  const fetchContent = async () => {
    try {
      let url = `${API}/education`;
      const params = new URLSearchParams();
      if (filters.category) params.append('category', filters.category);
      if (filters.difficulty_level) params.append('difficulty_level', filters.difficulty_level);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContent(response.data);
    } catch (error) {
      console.error('Failed to fetch content:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/education`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowForm(false);
      setFormData({
        title: '',
        description: '',
        content_type: 'article',
        difficulty_level: 'beginner',
        category: 'cognitive',
        content_url: '',
        thumbnail_url: '',
        duration_minutes: ''
      });
      fetchContent();
    } catch (error) {
      console.error('Failed to create content:', error);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const categoryLabels = {
    cognitive: 'Cognitif',
    motor: 'Moteur',
    social: 'Social',
    daily_living: 'Vie quotidienne'
  };

  const difficultyLabels = {
    beginner: 'Débutant',
    intermediate: 'Intermédiaire',
    advanced: 'Avancé'
  };

  const typeIcons = {
    video: Video,
    article: FileText,
    activity: Activity,
    exercise: BookOpen
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        <button
          data-testid="back-to-dashboard"
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 text-slate-600 hover:text-primary mb-3 transition-colors text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour au tableau de bord
        </button>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 md:mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-primary-900 font-heading">
            Contenu éducatif
          </h1>
          <Button
            data-testid="add-content-btn"
            onClick={() => setShowForm(!showForm)}
            className="w-full sm:w-auto"
          >
            <Plus className="w-5 h-5 mr-2" />
            {showForm ? 'Annuler' : 'Nouveau contenu'}
          </Button>
        </div>

        {/* Filters */}
        <Card className="mb-6 md:mb-8">
          <CardContent className="p-4 md:p-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
              <Filter className="w-5 h-5 text-slate-600 hidden sm:block" />
              <select
                value={filters.category}
                onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                className="w-full sm:w-auto rounded-xl border-slate-200 bg-white px-4 py-2 text-sm"
              >
                <option value="">Toutes les catégories</option>
                {Object.entries(categoryLabels).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
              <select
                value={filters.difficulty_level}
                onChange={(e) => setFilters({ ...filters, difficulty_level: e.target.value })}
                className="w-full sm:w-auto rounded-xl border-slate-200 bg-white px-4 py-2 text-sm"
              >
                <option value="">Tous les niveaux</option>
                {Object.entries(difficultyLabels).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
          </CardContent>
        </Card>

        {showForm && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <Card>
              <CardHeader>
                <CardTitle>Nouveau contenu éducatif</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Titre
                    </label>
                    <Input
                      data-testid="content-title-input"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Description
                    </label>
                    <Textarea
                      data-testid="content-description-input"
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Type
                      </label>
                      <select
                        name="content_type"
                        value={formData.content_type}
                        onChange={handleChange}
                        className="flex w-full rounded-xl border-slate-200 bg-white px-4 py-3"
                      >
                        <option value="video">Vidéo</option>
                        <option value="article">Article</option>
                        <option value="activity">Activité</option>
                        <option value="exercise">Exercice</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Catégorie
                      </label>
                      <select
                        name="category"
                        value={formData.category}
                        onChange={handleChange}
                        className="flex w-full rounded-xl border-slate-200 bg-white px-4 py-3"
                      >
                        {Object.entries(categoryLabels).map(([key, label]) => (
                          <option key={key} value={key}>{label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Niveau
                      </label>
                      <select
                        name="difficulty_level"
                        value={formData.difficulty_level}
                        onChange={handleChange}
                        className="flex w-full rounded-xl border-slate-200 bg-white px-4 py-3"
                      >
                        {Object.entries(difficultyLabels).map(([key, label]) => (
                          <option key={key} value={key}>{label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      URL du contenu (optionnel)
                    </label>
                    <Input
                      name="content_url"
                      value={formData.content_url}
                      onChange={handleChange}
                      placeholder="https://..."
                    />
                  </div>
                  <Button data-testid="submit-content-btn" type="submit" className="w-full">
                    Créer le contenu
                  </Button>
                </form>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {content.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <BookOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-slate-900 mb-2">Aucun contenu</h3>
              <p className="text-slate-600 mb-6">Commencez par ajouter du contenu éducatif</p>
              <Button onClick={() => setShowForm(true)}>
                <Plus className="w-5 h-5 mr-2" />
                Nouveau contenu
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {content.map((item) => {
              const Icon = typeIcons[item.content_type] || BookOpen;
              return (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <Card className="hover:-translate-y-1 h-full">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4 mb-4">
                        <div className="p-3 bg-secondary-50 rounded-full">
                          <Icon className="w-6 h-6 text-secondary" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-bold text-primary-900 mb-1">
                            {item.title}
                          </h3>
                          <div className="flex gap-2 flex-wrap">
                            <span className="text-xs px-2 py-1 bg-primary-50 text-primary rounded-full">
                              {categoryLabels[item.category]}
                            </span>
                            <span className="text-xs px-2 py-1 bg-accent/20 text-slate-700 rounded-full">
                              {difficultyLabels[item.difficulty_level]}
                            </span>
                          </div>
                        </div>
                      </div>
                      <p className="text-slate-600 text-sm mb-4">{item.description}</p>
                      {item.content_url && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full"
                          onClick={() => window.open(item.content_url, '_blank')}
                        >
                          Accéder au contenu
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default EducationPage;
